/**
 * Factory do client Supabase compartilhada entre os apps.
 * Cada app chama com suas env vars + o storage adapter apropriado.
 */
import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@pps/supabase";
import type { AuthStorage } from "./types";

export interface CreateSupabaseClientConfig {
  url: string;
  anonKey: string;
  storage: AuthStorage;
  /**
   * Override opcional do fluxo de auth. `detectSessionInUrl: true` é crucial
   * para OAuth callbacks e deep links do Capacitor (`playprosport://auth?code=...`).
   */
  detectSessionInUrl?: boolean;
  /**
   * Chave da sessão no storage. Default do supabase-js
   * (`sb-<project-ref>-auth-token`) quando omitido. A aba de suporte
   * (impersonação) usa `IMPERSONATION_AUTH_STORAGE_KEY` para nunca colidir
   * com a sessão real guardada no mesmo origin.
   */
  storageKey?: string;
}

export type PpsSupabaseClient = SupabaseClient<Database>;

export function createSupabaseClient(config: CreateSupabaseClientConfig): PpsSupabaseClient {
  const { url, anonKey, storage, detectSessionInUrl = true, storageKey } = config;

  if (!url || !anonKey) {
    throw new Error(
      "[@pps/auth-client] Missing Supabase credentials. Check VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY."
    );
  }

  return createClient<Database>(url, anonKey, {
    auth: {
      storage,
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl,
      ...(storageKey ? { storageKey } : {}),
    },
  });
}
